import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';

interface Answer {
  id: number;
  testo: string;
}

interface Question {
  id: number;
  text: string;
  answers?: Answer[];
}

const QuestionFlowForm: React.FC = () => {
  const { register, handleSubmit, reset, watch, setValue } = useForm();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Answer[]>([]);
  const [associations, setAssociations] = useState<Question[]>([]);
  const [message, setMessage] = useState<string>("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">("");
  const [showMessage, setShowMessage] = useState<boolean>(false);
  
  const selectedAnswerId = watch("answere_id", "");
  const [associatedQuestion, setAssociatedQuestion] = useState<Question | null>(null);
  const [associationError, setAssociationError] = useState<string>("");

  const MAX_OPTION_LENGTH = 50;
  const truncateText = (text: string): string => {
    return text.length > MAX_OPTION_LENGTH
      ? `${text.substring(0, MAX_OPTION_LENGTH)}...`
      : text;
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const questionsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/questions`);
        const sortedQuestions = questionsResponse.data.sort((a: any, b: any) => b.id - a.id);
        setQuestions(sortedQuestions);

        const answersResponse = await axios.get(`${process.env.REACT_APP_API_URL}/answers`);
        const sortedAnswers = answersResponse.data.sort((a: any, b: any) => b.id - a.id);
        setAnswers(sortedAnswers);

        const associationsResponse = await axios.get(`${process.env.REACT_APP_API_URL}/question_answers`);
        setAssociations(associationsResponse.data);
      } catch (error) {
        console.error("Errore durante il caricamento dei dati iniziali:", error);
      }
    };
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedAnswerId && associations.length > 0) {
      const assoc = associations.find(q =>
        q.answers && q.answers.some(a => a.id.toString() === selectedAnswerId.toString())
      );
      
      if (assoc) {
        setValue("question_id", assoc.id);
        setAssociatedQuestion(assoc);
      } else {
        setValue("question_id", "non_definito");
        setAssociatedQuestion(null);
      }
    }
  }, [selectedAnswerId, associations, setValue]);

  const showMessageWithTimeout = (msg: string, type: "success" | "error") => {
    setMessage(msg);
    setMessageType(type);
    setShowMessage(true);
    
    setTimeout(() => {
      setShowMessage(false);
      setMessage("");
      setMessageType("");
    }, 2000);
  };

  const onSubmit = async (data: any) => {
    try {
      const isAssociated = associations.some(q =>
        q.id === data.question_id &&
        q.answers && q.answers.some(a => a.id === parseInt(data.answere_id))
      );

      if (!isAssociated) {
        showMessageWithTimeout("La domanda e la risposta selezionate non sono associate.", "error");
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/question_nexts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error("Errore nella risposta del server");
      }

      showMessageWithTimeout("Flusso registrato con successo!", "success");
      reset();
    } catch (error: any) {
      showMessageWithTimeout("Errore durante la registrazione del flusso.", "error");
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="max-w-xl mx-auto bg-white rounded-lg shadow-md">
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-t-lg p-4">
        <h2 className="font-bold text-xl text-center">Definire il Flusso delle Domande</h2>
      </div>

      {showMessage && message && (
        <div 
          className={`mt-3 px-4 py-2 rounded-lg transition-opacity duration-300 ${messageType === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
          style={{ opacity: showMessage ? 1 : 0 }}
        >
          {message}
        </div>
      )}

      {associationError && (
        <div className="mt-3 px-4 py-2 bg-red-100 text-red-700 rounded-lg">
          {associationError}
        </div>
      )}

      <div className="space-y-4 p-4">
        <select
          {...register("question_id")}
          className="border border-gray-300 rounded-md w-full p-2 focus:outline-none focus:ring-2 focus:ring-blue-400 text-base"
          required
        >
          <option value="">Selezionare una domanda</option>
          <option value="non_definito">Non definito</option>
          {questions.map(q => (
            <option
              key={q.id}
              value={q.id}
              className="truncate"
              title={q.text}
            >
              {truncateText(q.text)}
            </option>
          ))}
        </select>

        <select
          {...register("answere_id")}
          className="border border-gray-300 rounded-md w-full p-2 focus:outline-none focus:ring-2 focus:ring-blue-400 text-base"
          required
        >
          <option value="">Selezionare una risposta</option>
          {answers.map(a => (
            <option
              key={a.id}
              value={a.id}
              className="truncate"
              title={a.testo}
            >
              {truncateText(a.testo)}
            </option>
          ))}
        </select>

        <select
          {...register("question_next_id")}
          className="border border-gray-300 rounded-md w-full p-2 focus:outline-none focus:ring-2 focus:ring-blue-400 text-base"
          required
        >
          <option value="">Selezionare la domanda successiva</option>
          {questions.map(q => (
            <option
              key={q.id}
              value={q.id}
              className="truncate"
              title={q.text}
            >
              {truncateText(q.text)}
            </option>
          ))}
        </select>

        <input
          {...register("condition")}
          placeholder="Condizione"
          className="border border-gray-300 rounded-md w-full p-2 mt-2 focus:outline-none focus:ring-2 focus:ring-blue-400 text-base"
        />

        <input
          {...register("order")}
          type="number"
          placeholder="Ordre"
          className="border border-gray-300 rounded-md w-full p-2 mt-2 focus:outline-none focus:ring-2 focus:ring-blue-400 text-base"
          required
        />

        <button
          type="submit"
          className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-indigo-600 hover:to-purple-700 flex items-center justify-center transition-all mt-4"
        >
          Registrare
        </button>
      </div>
    </form>
  );
};

export default QuestionFlowForm;